
import Vue from 'vue';
import Component from 'vue-class-component';
import { Watch } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { IOnOffHandlers } from 'bonomo';
import { IModule, ISubmodule } from '@/model/Community/communityInterfaces';
import { IErrors } from '@/model/IErrors';
import CreateButton from '@/components/shared/CreateButton.vue';

const communityModule = namespace('communityModule');

interface IconEntry { name: string; label: string; }
interface ICommunityRef { id: number; name: string; }

const ALL_ICONS: IconEntry[] = [
  { name: 'mdi-package-variant-closed', label: 'Package' },
  { name: 'mdi-cube-outline', label: 'Cube' },
  { name: 'mdi-view-grid-outline', label: 'Grid' },
  { name: 'mdi-tablet', label: 'Tablet' },
  { name: 'mdi-cellphone', label: 'Mobile' },
  { name: 'mdi-cellphone-text', label: 'App' },
  { name: 'mdi-lock-outline', label: 'Lock' },
  { name: 'mdi-lock-open-variant-outline', label: 'Locker' },
  { name: 'mdi-cloud-outline', label: 'Cloud' },
  { name: 'mdi-cloud-upload-outline', label: 'Cloud upload' },
  { name: 'mdi-server-outline', label: 'Server' },
  { name: 'mdi-web', label: 'Web' },
  { name: 'mdi-earth', label: 'Earth' },
  { name: 'mdi-store-outline', label: 'Store' },
  { name: 'mdi-office-building-outline', label: 'Office' },
  { name: 'mdi-home-outline', label: 'Home' },
  { name: 'mdi-rocket-launch-outline', label: 'Rocket' },
  { name: 'mdi-puzzle-outline', label: 'Puzzle' },
  { name: 'mdi-cog-outline', label: 'Settings' },
  { name: 'mdi-chip', label: 'Chip' },
  { name: 'mdi-shield-outline', label: 'Shield' },
  { name: 'mdi-star-outline', label: 'Star' },
  { name: 'mdi-lightning-bolt-outline', label: 'Lightning' },
  { name: 'mdi-briefcase-outline', label: 'Briefcase' },
  { name: 'mdi-database-outline', label: 'Database' },
  { name: 'mdi-api', label: 'API' },
  { name: 'mdi-code-braces', label: 'Code' },
  { name: 'mdi-monitor-outline', label: 'Monitor' },
  { name: 'mdi-robot-outline', label: 'Robot' },
  { name: 'mdi-tools', label: 'Tools' },
  { name: 'mdi-wrench-outline', label: 'Wrench' },
  { name: 'mdi-cart-outline', label: 'Cart' },
  { name: 'mdi-currency-eur', label: 'Euro' },
  { name: 'mdi-chart-line', label: 'Chart' },
  { name: 'mdi-account-group-outline', label: 'Users' },
  { name: 'mdi-account-cog-outline', label: 'Admin' },
  { name: 'mdi-email-outline', label: 'Email' },
  { name: 'mdi-bell-outline', label: 'Notifications' },
  { name: 'mdi-map-marker-outline', label: 'Location' },
  { name: 'mdi-calendar-outline', label: 'Calendar' },
];

type Mode = 'view' | 'createModule' | 'editModule';

@Component({
  name: 'Modules',
  components: { CreateButton },
})
export default class Modules extends Vue {

  @communityModule.Getter('availableModules')
  private availableModules!: IModule[];

  @communityModule.Getter('errors')
  private errors!: IErrors;

  @communityModule.Action('reloadModules')
  private reloadModules!: () => Promise<void>;

  @communityModule.Action('createModule')
  private createModule!: (data: {name: string; icon: string | null; description: string | null}) => Promise<any>;

  @communityModule.Action('updateModule')
  private updateModule!: (data: {id: number; name: string; icon: string | null; description: string | null}) => Promise<any>;

  @communityModule.Action('createSubmodule')
  private createSubmodule!: (data: {moduleId: number; name: string; description: string | null}) => void;

  @communityModule.Action('updateSubmodule')
  private updateSubmodule!: (data: {id: number; name: string; description: string | null}) => void;

  private loading: boolean = true;
  private mode: Mode = 'view';
  private icons: IconEntry[] = ALL_ICONS;
  private iconPickerOpen: boolean = false;
  private moduleForm: {name: string; icon: string; description: string} = {name: '', icon: '', description: ''};
  private moduleSaving: boolean = false;
  private moduleError: string = '';

  private editingSubmoduleId: number | 'new' | null = null;
  private submoduleForm: {name: string; description: string} = {name: '', description: ''};
  private submoduleSaving: boolean = false;
  private showSubmoduleErrors: boolean = false;
  private communitySearch: string = '';
  private subscribes: IOnOffHandlers = {};

  get selectedSlug(): string | null {
    return (this.$route.query.module as string) || null;
  }

  get selected(): IModule | null {
    const modules = this.availableModules || [];
    return modules.find((mod: IModule) => mod.slug === this.selectedSlug) || modules[0] || null;
  }

  get submoduleFormErrors(): IErrors {
    return this.showSubmoduleErrors && this.errors ? this.errors : {};
  }

  get submoduleNameErrors(): string[] {
    return [...(this.submoduleFormErrors.name || []), ...(this.submoduleFormErrors.slug || [])];
  }

  get filteredCommunities(): Array<ICommunityRef & {picks: string[]}> {
    if (!this.selected) return [];
    const picks: {[communityId: number]: string[]} = {};
    this.submodulesOf(this.selected).forEach((sub: ISubmodule) => {
      this.communitiesOf(sub).forEach((community: ICommunityRef) => {
        picks[community.id] = [...(picks[community.id] || []), sub.name];
      });
    });
    const query = (this.communitySearch || '').toLowerCase().trim();
    return this.communitiesOf(this.selected)
      .filter((community: ICommunityRef) => !query || community.name.toLowerCase().includes(query))
      .map((community: ICommunityRef) => ({ ...community, picks: picks[community.id] || [] }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  communitiesOf(item: IModule | ISubmodule): ICommunityRef[] {
    return item.communities || [];
  }

  submodulesOf(mod: IModule): ISubmodule[] {
    return mod.submodules || [];
  }

  created() {
    this.subscribes = this.$ons({
      'backend.call.begin.submoduleSaved': () => { this.submoduleSaving = true; },
      'backend.call.OK.submoduleSaved': this.onSubmoduleSaved,
      'backend.call.KO.submoduleSaved': () => {
        this.submoduleSaving = false;
        this.showSubmoduleErrors = true;
      },
    });
    this.reloadModules().finally(() => { this.loading = false; });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  @Watch('selectedSlug')
  onSelectedChange() {
    this.mode = 'view';
    this.communitySearch = '';
    this.closeSubmoduleForm();
  }

  selectModule(slug: string) {
    if (slug !== this.selectedSlug) {
      this.$router.replace({ query: { ...this.$route.query, module: slug } });
    } else {
      this.mode = 'view';
    }
  }

  startCreateModule() {
    this.moduleForm = {name: '', icon: '', description: ''};
    this.moduleError = '';
    this.iconPickerOpen = true;
    this.mode = 'createModule';
  }

  startEditModule() {
    if (!this.selected) return;
    this.moduleForm = {name: this.selected.name, icon: this.selected.icon || '', description: this.selected.description || ''};
    this.moduleError = '';
    this.iconPickerOpen = false;
    this.mode = 'editModule';
  }

  cancelModuleForm() {
    this.mode = 'view';
    this.focusButton('editModuleButton');
  }

  async saveModule() {
    if (!this.moduleForm.name.trim() || this.moduleSaving) return;
    const payload = {
      name: this.moduleForm.name.trim(),
      icon: this.moduleForm.icon || null,
      description: this.moduleForm.description.trim() || null,
    };
    this.moduleSaving = true;
    this.moduleError = '';
    try {
      if (this.mode === 'createModule') {
        const response = await this.createModule(payload);
        const created = response && response.data ? response.data : null;
        this.mode = 'view';
        if (created && created.slug) this.selectModule(created.slug);
      } else if (this.selected) {
        await this.updateModule({ id: this.selected.id, ...payload });
        this.mode = 'view';
      }
      this.focusButton('editModuleButton');
      this.$uiBus.emit('success', this.$t('common.saved'));
    } catch (e) {
      this.moduleError = this.$t('modules.saveFailed') as string;
    } finally {
      this.moduleSaving = false;
    }
  }

  startSubmodule(sub: ISubmodule | null) {
    this.showSubmoduleErrors = false;
    this.editingSubmoduleId = sub ? sub.id : 'new';
    this.submoduleForm = {name: sub ? sub.name : '', description: sub && sub.description ? sub.description : ''};
  }

  cancelSubmodule() {
    const focusTarget = this.submoduleFocusTarget();
    this.closeSubmoduleForm();
    this.focusButton(focusTarget);
  }

  private closeSubmoduleForm() {
    this.editingSubmoduleId = null;
    this.showSubmoduleErrors = false;
  }

  private submoduleFocusTarget(): string {
    return typeof this.editingSubmoduleId === 'number' ? 'editSubmodule' + this.editingSubmoduleId : 'addSubmoduleButton';
  }

  private focusButton(ref: string) {
    this.$nextTick(() => {
      const target = this.$refs[ref];
      const button = (Array.isArray(target) ? target[0] : target) as Vue | undefined;
      if (button && button.$el) (button.$el as HTMLElement).focus();
    });
  }

  saveSubmodule() {
    if (!this.submoduleForm.name.trim() || this.submoduleSaving || !this.selected) return;
    const payload = {name: this.submoduleForm.name.trim(), description: this.submoduleForm.description.trim() || null};
    if (this.editingSubmoduleId === 'new') {
      this.createSubmodule({ moduleId: this.selected.id, ...payload });
    } else if (this.editingSubmoduleId !== null) {
      this.updateSubmodule({ id: this.editingSubmoduleId, ...payload });
    }
  }

  private onSubmoduleSaved() {
    const focusTarget = this.submoduleFocusTarget();
    this.submoduleSaving = false;
    this.closeSubmoduleForm();
    this.focusButton(focusTarget);
    this.$uiBus.emit('success', this.$t('common.saved'));
    this.reloadModules();
  }
}
